import type { AwardCriterionDto, AwardLetterCriterionDto, AwardLetterScoreDto } from "../../../../autogen/bff-api";

export interface LabelledCriterion {
  id: string;
  name: string;
}

export interface CriterionScore {
  supplierPoints: number | null;
  winnerPoints: number | null;
}

interface NamedCriterion {
  id?: string | null;
  name?: string | null;
  parentName?: string | null;
}

export const awardNotificationCriteria = (
  evaluated: AwardLetterCriterionDto[],
  announced: AwardCriterionDto[],
  fallbackName: string,
): LabelledCriterion[] => {
  if (evaluated.length > 0) return evaluated.map((criterion) => labelOf(criterion, fallbackName));
  return identified(flattenAnnounced(announced)).map((criterion) => labelOf(criterion, fallbackName));
};

export const awardNotificationScores = (
  scores: Record<string, AwardLetterScoreDto>,
): Record<string, CriterionScore> | undefined => {
  const entries = Object.entries(scores);
  if (entries.length === 0) return undefined;
  return Object.fromEntries(
    entries.map(([id, score]) => [
      id,
      { supplierPoints: score.supplierPoints ?? null, winnerPoints: score.winnerPoints ?? null },
    ]),
  );
};

const flattenAnnounced = (announced: AwardCriterionDto[]): NamedCriterion[] =>
  announced.flatMap((criterion) => [
    { id: criterion.id, name: criterion.name, parentName: null },
    ...(criterion.subCriteria ?? []).map((sub) => ({
      id: sub.id,
      name: sub.name,
      parentName: criterion.name,
    })),
  ]);

const identified = (criteria: NamedCriterion[]): (NamedCriterion & { id: string })[] =>
  criteria.filter((criterion): criterion is NamedCriterion & { id: string } => !!criterion.id);

const labelOf = ({ id, name, parentName }: NamedCriterion & { id: string }, fallbackName: string): LabelledCriterion => {
  const own = name?.trim() || fallbackName;
  return { id, name: parentName ? `${parentName} — ${own}` : own };
};
